import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  Output,
  EventEmitter,
  HostBinding,
  AfterViewInit,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Actions, Store, ofActionSuccessful } from '@ngxs/store';
import { distinctUntilChanged, filter, Observable, Subscription, switchMap, tap } from 'rxjs';
import { ModuleNumbers } from 'src/app/core/enums/module-numbers.enum';
import { MenuItemModel } from 'src/app/core/models/menu-id.model';
import { CreateUpdateDeleteRecursiveArrayService } from 'src/app/core/services/create-update-delete-recursive-array.service';
import { MenuListState } from 'src/app/core/state/menu-list.state';
import { MenuTreeStructureState } from 'src/app/core/state/menu-tree-structure.state';
import { ComponentsStructureState } from 'src/app/maintenance-admin/state/components-structure.state';
import { CdkDropList, CdkDropListGroup } from '@angular/cdk/drag-drop';
import {
  GetMaintenanceMenuStructure,
  TransferMaintenanceComponent,
  UpdateComponentsStructureOrder,
} from 'src/app/maintenance-admin/state/components-structure.actions';
import { ALL_DEPS_TOKEN } from 'src/app/tenant-selector/state/tenant-user-access.state';
import { MenuDragDropService } from '../menu-drag-drop.service';
import {
  LoadSpecificMenuStructure,
  LoadSpecificMenuStructureForTenant,
  SetActiveMenuElement,
  UpdateMenuStructureOrder,
} from '../../../core/state/menu-tree-structure.actions';

const TENANT_WIDE_MODULES = [ModuleNumbers.StandardJobTemplates];
const MODULES_WITHOUT_MENUS = [ModuleNumbers.Order, ModuleNumbers.CrewManagement];
const TOP_LEVEL_ITEMS_PER_PAGE = 30;
const SCROLL_CONTAINER_TOP_OFFSET_REM = 1;

@Component({
  selector: 'app-menu-tree-structure',
  templateUrl: './menu-tree-structure.component.html',
  styleUrls: ['./menu-tree-structure.component.scss'],
  standalone: false,
})
export class MenuTreeStructureComponent implements OnInit, OnDestroy, AfterViewInit {
  getEntireMenuStructure$: Observable<MenuItemModel[]> = this.store
    .select(MenuTreeStructureState.getEntireMenuStructure)
    .pipe(
      tap((data) => {
        this.menuDNDService.setInitialMenuStructure(data);
      })
    );
  getModuleNumber$: Observable<number | undefined> = this.store.select(
    MenuListState.getModuleNumber
  );
  maintenanceMenuStructure$: Observable<any[]> = this.store
    .select(ComponentsStructureState.getComponentsStructure)
    .pipe(
      tap((data) => {
        this.menuDNDService.setInitialMenuStructure(data as any[]);
      })
    );

  structurePreview: MenuItemModel[] = [];
  activeElementId$: Observable<string> = this.store.select(
    MenuTreeStructureState.activeMenuElementId
  );
  loaded$: Observable<boolean> = this.store.select(MenuTreeStructureState.menuStructureLoaded);
  actions = [UpdateComponentsStructureOrder, UpdateMenuStructureOrder];

  @Input() showFunctionalButtons = true;
  @Input() isRootAddEnabled = false;
  @Input() menuItemsAsRouting = false;
  @Input() moduleNumber: ModuleNumbers;
  @Input() menuMode = false;
  @Input() depId: string;
  @Input() hideIfNoItems = false;
  @Input() adminPrivilege: string;
  @ViewChild(CdkDropListGroup) listGroup: CdkDropList<CdkDropListGroup<any>>;
  @ViewChild('scrollContainer') scrollContainerRef: ElementRef<HTMLElement>;
  @Output() readonly menuItemClicked: EventEmitter<string> = new EventEmitter<string>();

  visibleTopLevelCount = TOP_LEVEL_ITEMS_PER_PAGE;
  scrollContainerMaxHeightPx: number | null = null;
  contentExceedsContainer = false;
  private scrollHandler: (() => void) | null = null;
  private lastLoadMoreEmit = 0;
  private readonly loadMoreThrottleMs = 300;
  private resizeObserver: ResizeObserver | null = null;
  private mutationObserver: MutationObserver | null = null;
  private lastStructureSignature: string | null = null;
  private scrollToTopOnNextLayout = false;

  @HostBinding('class.menu-tree-structure-hidden') get hideIfNoItemsBinding() {
    return this.hideIfNoItems && this.structurePreview.length === 0;
  }

  get shouldUseInfiniteScroll(): boolean {
    return this.menuMode && this.menuItemsAsRouting;
  }

  get visibleStructurePreview(): MenuItemModel[] {
    if (!this.shouldUseInfiniteScroll) {
      return this.structurePreview;
    }
    return this.structurePreview.slice(0, this.visibleTopLevelCount);
  }

  get hasMoreTopLevelItems(): boolean {
    return this.shouldUseInfiniteScroll && this.structurePreview.length > this.visibleTopLevelCount;
  }

  readonly ALL_DEPS_TOKEN = ALL_DEPS_TOKEN;
  tenantId: string;
  module: number;
  moduleNumbers = ModuleNumbers;
  chosenElementId = '';
  isProductOwner = false;

  private subscriptions = new Subscription();

  constructor(
    private store: Store,
    private router: Router,
    public menuDNDService: MenuDragDropService,
    private actions$: Actions,
    private elementRef: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef
  ) {
    this.subscriptions.add(
      this.menuDNDService.structurePreview.subscribe((structure) => {
        this.structurePreview = structure;
        if (this.shouldUseInfiniteScroll) {
          const signature = this.getStructureSignature(structure);
          if (this.lastStructureSignature !== signature) {
            this.lastStructureSignature = signature;
            this.visibleTopLevelCount = TOP_LEVEL_ITEMS_PER_PAGE;
            this.scrollToTopOnNextLayout = true;
          }
          setTimeout(() => {
            this.addScrollListener();
            this.updateScrollContainerHeight();
            this.setupResizeObserver();
            if (this.scrollToTopOnNextLayout && this.scrollContainerRef?.nativeElement) {
              this.scrollContainerRef.nativeElement.scrollTop = 0;
              this.scrollToTopOnNextLayout = false;
            }
          }, 150);
        }
      })
    );
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          switchMap(() => this.getEntireMenuStructure$),
          filter((data) => !!data.length),
          filter((data) => {
            this.chosenElementId = this.isAnyElementChosen(data, this.router.url);
            this.store.dispatch(new SetActiveMenuElement(this.chosenElementId));
            return !this.chosenElementId;
          }),
          switchMap(() => this.activeElementId$),
          tap((activeElementId) => {
            this.chosenElementId = activeElementId;
          })
        )
        .subscribe()
    );
    this.subscriptions.add(
      this.getModuleNumber$.pipe(distinctUntilChanged()).subscribe((moduleNumber) => {
        if (
          moduleNumber &&
          (moduleNumber !== ModuleNumbers.Document || !this.showFunctionalButtons)
        ) {
          this.module = moduleNumber;
          if (TENANT_WIDE_MODULES.includes(this.module)) {
            this.store.dispatch(new LoadSpecificMenuStructureForTenant(this.module));
          } else if (!MODULES_WITHOUT_MENUS.includes(this.module)) {
            this.store.dispatch(new LoadSpecificMenuStructure(this.module));
          }
        }
      })
    );
    this.subscriptions.add(
      (this.moduleNumber === ModuleNumbers.Maintenance
        ? this.maintenanceMenuStructure$
        : this.getEntireMenuStructure$
      ).subscribe()
    );
    this.subscriptions.add(
      this.actions$.pipe(ofActionSuccessful(TransferMaintenanceComponent)).subscribe(() => {
        this.store.dispatch(new GetMaintenanceMenuStructure());
      })
    );
  }

  ngAfterViewInit(): void {
    if (this.shouldUseInfiniteScroll && this.structurePreview.length > 0) {
      setTimeout(() => {
        this.addScrollListener();
        this.updateScrollContainerHeight();
        this.setupResizeObserver();
      }, 100);
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.mutationObserver?.disconnect();
    this.mutationObserver = null;
    this.removeScrollListener();
    this.subscriptions.unsubscribe();
  }

  loadMoreTopLevelItems(): void {
    this.visibleTopLevelCount += TOP_LEVEL_ITEMS_PER_PAGE;
    setTimeout(() => this.updateScrollContainerHeight(), 50);
  }

  private setupResizeObserver(): void {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    const parent = this.elementRef.nativeElement.closest('.mat-drawer-inner-container');
    const container = this.scrollContainerRef?.nativeElement;
    if (!parent || !container || !this.shouldUseInfiniteScroll) {
      return;
    }
    this.resizeObserver = new ResizeObserver(() => {
      this.updateScrollContainerHeight();
    });
    this.resizeObserver.observe(parent);
    this.mutationObserver = new MutationObserver(() => {
      setTimeout(() => this.updateScrollContainerHeight(), 50);
    });
    this.mutationObserver.observe(container, {
      childList: true,
      subtree: true,
    });
  }

  private updateScrollContainerHeight(): void {
    if (!this.shouldUseInfiniteScroll) {
      return;
    }
    const parent = this.elementRef.nativeElement.closest('.mat-drawer-inner-container');
    const container = this.scrollContainerRef?.nativeElement;
    if (!parent || !container) {
      return;
    }
    const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
    const topOffsetPx = SCROLL_CONTAINER_TOP_OFFSET_REM * rootFontSize;
    const availableHeight = (parent as HTMLElement).clientHeight - topOffsetPx;
    this.scrollContainerMaxHeightPx = Math.max(0, availableHeight);
    const maxHeight = this.scrollContainerMaxHeightPx;
    requestAnimationFrame(() => {
      this.contentExceedsContainer = maxHeight !== null && container.scrollHeight > maxHeight;
      this.cdr.markForCheck();
    });
  }

  private addScrollListener(): void {
    this.removeScrollListener();
    setTimeout(() => {
      const container = this.scrollContainerRef?.nativeElement;
      if (!container || this.scrollHandler) {
        return;
      }
      this.scrollHandler = () => this.onScroll();
      container.addEventListener('scroll', this.scrollHandler);
    }, 0);
  }

  private removeScrollListener(): void {
    const container = this.scrollContainerRef?.nativeElement;
    if (container && this.scrollHandler) {
      container.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }
  }

  private onScroll(): void {
    const container = this.scrollContainerRef?.nativeElement;
    if (!this.hasMoreTopLevelItems || !container) {
      return;
    }
    const { scrollTop, clientHeight, scrollHeight } = container;
    const threshold = 50;
    if (scrollTop + clientHeight >= scrollHeight - threshold) {
      const now = Date.now();
      if (now - this.lastLoadMoreEmit < this.loadMoreThrottleMs) {
        return;
      }
      this.lastLoadMoreEmit = now;
      this.loadMoreTopLevelItems();
    }
  }

  highlightMenuItem(itemId: string) {
    if (!this.showFunctionalButtons) {
      this.chosenElementId = itemId;
    }
    this.menuItemClicked.emit(itemId);
  }

  private isAnyElementChosen(items: MenuItemModel[], url: string): string {
    const flatItems = CreateUpdateDeleteRecursiveArrayService.getFlatList(items);
    return flatItems.find((item) => url.includes(item.id))?.id || '';
  }

  private getStructureSignature(structure: MenuItemModel[]): string {
    if (!structure?.length) {
      return '';
    }
    const first = structure[0]?.id ?? '';
    const last = structure[structure.length - 1]?.id ?? '';
    return `${structure.length}:${first}:${last}`;
  }
}
